import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { fetchCurrentUser } from '@/api/auth';
import { getNotifications } from '@/api/notifications';
import { Sidebar } from '@/components/layout/sidebar/sidebar';
import { useAuthStore } from '@/stores/authStore';

import { AnimatedBackground } from './AnimatedBackground';
import { DeadlineReminder } from './deadline-reminder';
import styles from './app-shell.module.css';

export function AppShell() {
  const navigate = useNavigate();
  const token = useAuthStore((state) => state.token);
  const setAuth = useAuthStore((state) => state.setAuth);
  const clearAuth = useAuthStore((state) => state.clearAuth);

  // 启动时校验 token 与 user 是否一致，防止数据库重置后本地旧 token 指向其它角色
  useEffect(() => {
    if (!token) return;
    fetchCurrentUser()
      .then((user) => {
        setAuth(user, token);
      })
      .catch(() => {
        clearAuth();
        navigate('/login', { replace: true });
      });
  }, [token, setAuth, clearAuth, navigate]);

  // 轮询未读消息数（每 30 秒）
  const { data } = useQuery({
    queryKey: ['notifications', 'unread'],
    queryFn: () => getNotifications(1),
    refetchInterval: 30_000,
  });
  const unread = data?.unread_count ?? 0;

  return (
    <div className={styles.shell}>
      <Sidebar />
      <main className={styles.main}>
        <AnimatedBackground />
        <header className={styles.topbar}>
          <button
            type="button"
            className={styles.bellBtn}
            onClick={() => navigate('/notifications')}
            aria-label={`消息中心（${unread} 条未读）`}
            title="消息中心"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            {unread > 0 && (
              <span className={styles.bellBadge}>{unread > 99 ? '99+' : unread}</span>
            )}
          </button>
        </header>
        <div className={styles.content}>
          <Outlet />
        </div>
      </main>
      <DeadlineReminder />
    </div>
  );
}
